import styled, { css, keyframes } from 'styled-components';

import {
  DOWNWARD_PANEL_QUERY,
  QUESTION_PANEL_BLOCK_HEIGHT,
  QUESTION_PANEL_BLOCK_HEIGHT_STACKED,
  SMALL_SCREEN_QUERY,
} from '@/utils/constants';

export const RISE = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const CategoryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  @media ${SMALL_SCREEN_QUERY} {
    flex-direction: column;
    width: 100%;

    > * {
      width: 100%;
      justify-content: center;
    }
  }

  > * {
    animation: ${RISE} 320ms ease both;
  }

  > *:nth-child(1) {
    animation-delay: 40ms;
  }
  > *:nth-child(2) {
    animation-delay: 100ms;
  }
  > *:nth-child(3) {
    animation-delay: 160ms;
  }

  @media (prefers-reduced-motion: reduce) {
    > * {
      animation: none;
    }
  }
`;

export const PanelCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 3px;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid var(--gray-light);
  background: var(--color-white);
  color: var(--gray-dark);
  font-size: 12px;
  cursor: pointer;
  transition:
    background 160ms ease,
    color 160ms ease,
    border-color 160ms ease;

  &:hover {
    background: var(--gray-lightest);
    color: var(--gray-darkest);
    border-color: var(--gray);
  }
`;

export const PanelToolbarRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
`;

export const PanelToolbarControls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  flex: 1 1 auto;
  gap: 10px;
  min-width: 0;

  @media ${SMALL_SCREEN_QUERY} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const QuestionsListWrapper = styled.div`
  //INFO: A fixed responsive height keeps the panel from jumping when filtering or searching changes
  //the number of results, including down to none.
  height: clamp(140px, 25vh, 260px);
  overflow-y: auto;

  .list-group {
    margin-bottom: 0;
  }

  &::-webkit-scrollbar {
    width: 8px;
    background-color: white;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 4px;
  }

  scrollbar-width: thin;
  scrollbar-color: #ccc white;
`;

export const PanelSearchWrapper = styled.div`
  flex: 1 1 160px;
  min-width: 140px;
  max-width: 220px;

  @media ${SMALL_SCREEN_QUERY} {
    flex: 0 0 auto;
    width: 100%;
    max-width: none;
  }
`;

/**
 * Makes the open Question Panel fill the block reserved for it below the Chat Composer instead of sizing
 * to its content. The height comes from the same constant the Centred Layout reserves as padding, so the
 * reservation and the panel can never disagree.
 *
 * Gated on DOWNWARD_PANEL_QUERY rather than CENTRED_LAYOUT_QUERY: the panel is sized for the block
 * whenever it is docked below the composer, which includes the band where the block is top-aligned rather
 * than centred. In that band there is no reservation to agree with, and the fixed height is what keeps the
 * panel from growing or shrinking as filtering and searching change the number of results.
 */
const filledBlock = (isOpen: boolean) => css`
  @media ${DOWNWARD_PANEL_QUERY} {
    display: flex;
    flex-direction: column;
    height: ${isOpen ? QUESTION_PANEL_BLOCK_HEIGHT : '0'};
    max-height: none;
    overflow: hidden;

    @media ${SMALL_SCREEN_QUERY} {
      height: ${isOpen ? QUESTION_PANEL_BLOCK_HEIGHT_STACKED : '0'};
    }

    ${PanelToolbarRow} {
      flex: 0 0 auto;
    }

    ${QuestionsListWrapper} {
      flex: 1 1 auto;
      height: auto;
      min-height: 0;
    }
  }
`;

export const PanelWrapper = styled.div<{ $open: boolean; $isBelowComposer: boolean }>`
  overflow: hidden;
  max-height: ${({ $open }) => ($open ? '58vh' : '0')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: translateY(${({ $open }) => ($open ? '0' : '8px')});
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  padding: ${({ $open }) => ($open ? '16px' : '0 16px')};
  background: var(--color-white);
  border: 1px solid var(--gray-light);
  border-radius: 18px;
  box-shadow: 0 14px 36px rgba(15, 42, 50, 0.14);
  overflow-y: auto;
  transition:
    opacity 400ms ease,
    transform 400ms ease,
    max-height 400ms ease,
    height 400ms ease,
    padding 400ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${({ $isBelowComposer, $open }) => $isBelowComposer && filledBlock($open)}
`;
